<template>
    <section class="ExpandableExample">
        <h2 class="ExpandableExample__title">
            Expandable
        </h2>
        <p class="ExpandableExample__desc">
            {{ desc }}
        </p>

        <h3 class="ExpandableExample__subsection">
            Trigger variants
        </h3>
        <table class="ExpandableExample__table">
            <thead>
                <tr>
                    <th class="ExpandableExample__th ExpandableExample__th--label">
                        Variant
                    </th>
                    <th class="ExpandableExample__th">
                        Preview (click to open)
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in triggerRows"
                    :key="row.label"
                    class="ExpandableExample__tr"
                >
                    <td class="ExpandableExample__td ExpandableExample__td--label">
                        {{ row.label }}
                    </td>
                    <td class="ExpandableExample__td">
                        <Expandable
                            :label="row.triggerLabel"
                            :icon="row.icon"
                            :disabled="row.disabled"
                            :menu-items="basicItems"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <h3 class="ExpandableExample__subsection">
            Menu item variants
        </h3>
        <table class="ExpandableExample__table">
            <thead>
                <tr>
                    <th class="ExpandableExample__th ExpandableExample__th--label">
                        Items
                    </th>
                    <th class="ExpandableExample__th">
                        Preview
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in menuRows"
                    :key="row.label"
                    class="ExpandableExample__tr"
                >
                    <td class="ExpandableExample__td ExpandableExample__td--label">
                        {{ row.label }}
                    </td>
                    <td class="ExpandableExample__td">
                        <Expandable label="Actions" :menu-items="row.items" />
                    </td>
                </tr>
            </tbody>
        </table>

        <h3 class="ExpandableExample__subsection">
            Custom trigger slot
        </h3>
        <div class="ExpandableExample__customDemo">
            <Expandable :menu-items="basicItems">
                <template #trigger="{ toggle }">
                    <NewButton variant="outline" color="neutral" size="sm" @click="toggle">
                        <EidIcon :icon-src="faEllipsis" />
                    </NewButton>
                </template>
            </Expandable>
        </div>
    </section>
</template>

<style lang="scss">
.ExpandableExample {
    padding: 1.5rem;
    max-width: 56rem;
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.ExpandableExample__title {
    margin: 0 0 0.25rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--eidTechGray);
}

.ExpandableExample__desc {
    margin: 0 0 1.5rem;
    font-size: 0.8125rem;
    color: var(--eidTechGray-75);
}

.ExpandableExample__subsection {
    margin: 1.5rem 0 0.75rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--eidTechGray);
}

.ExpandableExample__table {
    border-collapse: collapse;
    width: 100%;
}

.ExpandableExample__th {
    padding: 0.5rem 1rem 0.5rem 0;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--eidTechGray);
    text-align: left;

    &--label {
        width: 12rem;
    }
}

.ExpandableExample__tr {
    border-top: 1px solid var(--eidTechGray-10);
}

.ExpandableExample__td {
    padding: 0.75rem 1rem 0.75rem 0;
    vertical-align: middle;

    &--label {
        font-size: 0.8125rem;
        color: var(--eidTechGray-75);
    }
}

.ExpandableExample__customDemo {
    padding: 1rem 0;
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faCopy, faEllipsis, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import Expandable from '@/components/expandable/Expandable.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import { ExpandableMenuItem } from '@/models/ExpandableMenuItemConfig.model';

    interface TriggerRow {
        label: string;
        triggerLabel: string;
        icon: IconDefinition | null;
        disabled: boolean;
    }

    interface MenuRow {
        label: string;
        items: ExpandableMenuItem[];
    }

    function noop(): void {
        return undefined;
    }

    const BASIC_ITEMS: ExpandableMenuItem[] = [
        { label: 'Edit', action: noop },
        { label: 'Duplicate', action: noop },
        { label: 'Delete', action: noop },
    ];

    const ICON_ITEMS: ExpandableMenuItem[] = [
        { label: 'Edit', icon: faPen, action: noop },
        { label: 'Duplicate', icon: faCopy, action: noop },
        { label: 'Delete', icon: faTrash, action: noop },
    ];

    const DISABLED_ITEMS: ExpandableMenuItem[] = [
        { label: 'Edit', icon: faPen, action: noop },
        { label: 'Duplicate', icon: faCopy, disabled: true, action: noop },
        { label: 'Delete', icon: faTrash, action: noop },
    ];

    const TRIGGER_ROWS: TriggerRow[] = [
        { label: 'Label only', triggerLabel: 'Actions', icon: null, disabled: false },
        { label: 'Icon + label', triggerLabel: 'Actions', icon: faPen, disabled: false },
        { label: 'Disabled', triggerLabel: 'Actions', icon: null, disabled: true },
        { label: 'Disabled (icon + label)', triggerLabel: 'Actions', icon: faPen, disabled: true },
    ];

    const MENU_ROWS: MenuRow[] = [
        { label: 'Plain', items: BASIC_ITEMS },
        { label: 'With icons', items: ICON_ITEMS },
        { label: 'With disabled item', items: DISABLED_ITEMS },
    ];

    export default defineComponent({
        name: 'ExpandableExample',
        components: { Expandable, NewButton, EidIcon },
        data() {
            return {
                desc: 'Dropdown menu anchored to a trigger button. Supports label/icon triggers, custom trigger slots, and menu items with icons or disabled states.',
                faEllipsis,
                triggerRows: TRIGGER_ROWS,
                menuRows: MENU_ROWS,
                basicItems: BASIC_ITEMS,
            };
        },
    });
</script>
